import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ProgramsHero() {
  const [scrolled, setScrolled] = useState(false);
  const badgeRef = useScrollAnimation({ delay: 0 });
  const headlineRef = useScrollAnimation({ delay: 100 });
  const subheadlineRef = useScrollAnimation({ delay: 200 });
  const ctaRef = useScrollAnimation({ delay: 300 });
  const statsRef = useScrollAnimation({ delay: 400 });

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToPackages = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleBookCall = () => {
    const ctaSection = document.getElementById('cta-section');
    if (ctaSection) {
      ctaSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const stats = [
    { number: '500+', label: 'Clients Transformed' },
    { number: '4.9★', label: 'Average Rating' },
    { number: '95%', label: 'Success Rate' },
    { number: '12 Weeks', label: 'Avg. Transformation' }
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden" style={{ background: '#000000' }}>
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #B8860B 0%, transparent 50%), radial-gradient(circle at 80% 20%, #8B0000 0%, transparent 50%)' }}></div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24 lg:py-32">
        <div className="text-center">
          {/* Urgency Badge */}
          <div ref={badgeRef} className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 sm:mb-8" style={{ background: 'rgba(139,0,0,0.2)', border: '1px solid rgba(139,0,0,0.4)' }}>
            <div className="w-2 h-2 rounded-full bg-[#8B0000] animate-pulse"></div>
            <span className="text-xs sm:text-sm font-medium" style={{ color: '#ff6b6b' }}>Limited Spots Available This Month</span>
          </div>

          {/* Main Headline */}
          <h1 ref={headlineRef} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 leading-tight" style={{ color: '#ffffff' }}>
            Stop Wishing.
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#B8860B] to-[#FFD700]">
              Start Building.
            </span>
          </h1>

          {/* Subheadline */}
          <p ref={subheadlineRef} className="text-lg sm:text-xl md:text-2xl max-w-3xl mx-auto mb-10 sm:mb-12 leading-relaxed px-4" style={{ color: '#cccccc' }}>
            Choose the transformation package that fits your goals. Every program is designed to deliver real results, backed by proven systems and personal accountability.
          </p>

          {/* CTA Buttons */}
          <div ref={ctaRef} className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 mb-16 sm:mb-20 px-4">
            <button
              onClick={scrollToPackages}
              className="w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-[#B8860B] to-[#FFD700] text-black text-base sm:text-lg font-bold rounded-lg hover:shadow-2xl hover:shadow-[#B8860B]/40 transition-all duration-300 transform hover:scale-105 whitespace-nowrap"
            >
              See All Packages
            </button>
            <button
              onClick={handleBookCall}
              className="w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 text-base sm:text-lg font-bold rounded-lg border-2 transition-all duration-300 whitespace-nowrap"
              style={{ background: 'transparent', border: '2px solid #ffffff', color: '#ffffff' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#ffffff'; e.currentTarget.style.color = '#000000'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ffffff'; }}
            >
              Book Free Call
            </button>
          </div>

          {/* Social Proof Stats */}
          <div ref={statsRef} className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 max-w-5xl mx-auto px-4">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="rounded-xl p-4 sm:p-6 hover:shadow-lg transition-all duration-300"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(184,134,11,0.3)' }}
              >
                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#B8860B] to-[#FFD700] mb-2">
                  {stat.number}
                </div>
                <div className="text-xs sm:text-sm font-medium" style={{ color: '#aaaaaa' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 rounded-full flex items-start justify-center p-2" style={{ borderColor: 'rgba(184,134,11,0.5)' }}>
          <div className="w-1.5 h-3 bg-[#B8860B] rounded-full"></div>
        </div>
      </div>
    </section>
  );
}